/** @jsxRuntime automatic @jsxImportSource react */ // [C35] the PDF worker loads these files through tsx outside its own tsconfig
// [C35] Thermal 80 mm voucher (D-064 (1), D-149) — a NEW layout: the old application printed every voucher on A4 and had
// no 80 mm template, so there is no visual reference and this needs the owner's own sign-off (D-064; deviation V-95).
// Designed in the existing visual identity of the A4 customer entry voucher (`CustomerEntryVoucherA4.tsx`, from
// `CustomerReceiptVoucher.tsx:298-415`): the office block in black Cairo, the double rule, the subject badge in a
// black-bordered rounded pill, label / value rows in a black-ruled table, the amount row emphasised, the operator /
// signature footer. Adapted to a thermal printer: 72 mm printable width (80 mm paper, 4 mm margins), one column,
// black on white only (tints print as noise on thermal paper), dashed tear rules, sizes 9 – 15 px.
//
// One template serves the four single-operation vouchers of D-149 (customer receipt, cash transfer, installment
// payment, store invoice); the builders in `voucher80-builders.ts` turn each voucher's A4 props into these props, so
// the 80 mm copy always carries exactly the figures of the A4 copy. Presentational only: every label arrives
// preformatted; the amount in words is the server's (D-148).
import { createT } from '@faylasuf/i18n';
import type { Locale } from '@faylasuf/i18n';
import type { PrintOffice } from './StatementA4.js';

export interface Voucher80Row {
  readonly label: string;
  readonly value: string;
  /** Emphasised row (the amount, the balance after). */
  readonly strong?: boolean;
  /** Figures and codes: monospaced, left-to-right. */
  readonly mono?: boolean;
}

export interface Voucher80Item {
  readonly name: string;
  readonly quantity: string;
  readonly priceLabel: string;
  readonly totalLabel: string;
}

export interface Voucher80Props {
  readonly locale: Locale;
  readonly id?: string;
  readonly office: PrintOffice;
  /** Subject badge, e.g. "م/ وصل تسديد قسط". */
  readonly title: string;
  /** Printed document number (D-147); null while an operation waits for approval. */
  readonly numberLabel: string | null;
  readonly copy: boolean;
  /** Operation date in the office time zone (D-146). */
  readonly dateLabel: string;
  readonly timeLabel?: string | null;
  /** "Printed at" in the office time zone (D-146: operation date plus the print time). */
  readonly printedLabel?: string | null;
  readonly party?: { readonly label: string; readonly name: string; readonly sub?: string | null } | null;
  readonly rows: readonly Voucher80Row[];
  readonly items?: readonly Voucher80Item[];
  /** The boxed main figure (amount paid, total). */
  readonly amount?: { readonly label: string; readonly value: string } | null;
  readonly totals?: readonly Voucher80Row[];
  /** D-148, from the server (one phrase per currency). */
  readonly words?: readonly string[];
  readonly note?: string | null;
  readonly operator: string;
}

export function Voucher80(p: Voucher80Props) {
  const t = createT(p.locale);
  const isAr = p.locale === 'ar';
  const phones = [p.office.phone1, p.office.phone2].filter((x): x is string => typeof x === 'string' && x.length > 0);
  return (
    <div
      id={p.id ?? 'voucher-80mm'}
      className="voucher-80mm bg-white text-black font-sans leading-snug mx-auto"
      dir={isAr ? 'rtl' : 'ltr'}
      style={{ width: '72mm', padding: '0', fontSize: '10px' }}
      data-testid="voucher-80mm"
    >
      {/* Header */}
      <div className="text-center pb-1">
        {p.office.logoUrl ? <img src={p.office.logoUrl} alt="" className="mx-auto mb-1 object-contain" style={{ height: '12mm', width: 'auto' }} /> : null}
        <h1 className="font-black text-black tracking-tight" style={{ fontSize: '15px', lineHeight: 1.3 }} data-testid="v80-office">
          {p.office.name}
        </h1>
        {p.office.address ? <p className="font-bold text-black" style={{ fontSize: '9px' }}>{p.office.address}</p> : null}
        {phones.length > 0 ? (
          <p className="font-mono font-bold text-black" style={{ fontSize: '9px' }} dir="ltr">
            {phones.join(' - ')}
          </p>
        ) : null}
      </div>

      {/* Double Divider Line */}
      <div className="border-t-2 border-b border-black my-1" style={{ height: '3px' }}></div>

      {/* Number / date block */}
      <div className="flex justify-between items-start font-bold" style={{ fontSize: '9.5px' }}>
        <div>
          <span className="font-normal">{t('print.v80.number')}: </span>
          <span className="font-mono font-black" dir="ltr" data-testid="v80-number">{p.numberLabel ?? '—'}</span>
        </div>
        <div className="text-left" dir="ltr">
          <span className="font-mono">{p.dateLabel}</span>
          {p.timeLabel ? <span className="font-mono"> {p.timeLabel}</span> : null}
        </div>
      </div>

      {/* Subject Badge */}
      <div className="text-center my-2">
        <div className="inline-block border-2 border-black rounded-2xl px-4 py-0.5 font-black" style={{ fontSize: '11.5px' }} data-testid="v80-title">
          {p.title}
        </div>
        {p.copy && (
          <div className="mt-1">
            <span className="inline-block border border-black rounded-full px-3 font-black" style={{ fontSize: '9px' }} data-testid="v80-copy">
              {t('print.voucher.copy')}
            </span>
          </div>
        )}
      </div>

      {/* Party */}
      {p.party ? (
        <div className="mb-1.5" style={{ fontSize: '10.5px' }}>
          <span className="font-bold">{p.party.label.replace(/[:：]\s*$/, '')}: </span>
          <span className="font-black underline">{p.party.name}</span>
          {p.party.sub ? <span className="font-mono" style={{ fontSize: '9px' }}> ({p.party.sub})</span> : null}
        </div>
      ) : null}

      {/* Rows */}
      {p.rows.length > 0 && (
        <table className="w-full border-collapse border border-black" style={{ fontSize: '9.5px' }}>
          <tbody>
            {p.rows.map((r, i) => (
              <tr key={i}>
                <td className="border border-black px-1.5 py-1 font-bold" style={{ width: '42%' }}>{r.label}</td>
                <td className={`border border-black px-1.5 py-1 text-center ${r.mono ? 'font-mono' : ''} ${r.strong ? 'font-black' : 'font-bold'}`} style={r.strong ? { fontSize: '11px' } : undefined} {...(r.mono ? { dir: 'ltr' } : {})}>
                  {r.value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Items (store invoice) */}
      {p.items && p.items.length > 0 && (
        <table className="w-full border-collapse mt-1.5" style={{ fontSize: '9px' }} data-testid="v80-items">
          <thead>
            <tr className="border-t-2 border-b border-black">
              <th className="py-0.5 text-start font-black">{t('print.v80.item')}</th>
              <th className="py-0.5 text-center font-black">{t('print.v80.qty')}</th>
              <th className="py-0.5 text-center font-black">{t('print.v80.price')}</th>
              <th className="py-0.5 text-end font-black">{t('print.v80.total')}</th>
            </tr>
          </thead>
          <tbody>
            {p.items.map((it, i) => (
              <tr key={i} className="border-b border-dashed border-black">
                <td className="py-0.5 font-bold" style={{ wordBreak: 'break-word' }}>{it.name}</td>
                <td className="py-0.5 text-center font-mono">{it.quantity}</td>
                <td className="py-0.5 text-center font-mono" dir="ltr">{it.priceLabel}</td>
                <td className="py-0.5 text-end font-mono font-bold" dir="ltr">{it.totalLabel}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Amount box */}
      {p.amount ? (
        <div className="border-2 border-black rounded-lg mt-2 px-2 py-1 flex justify-between items-center" data-testid="v80-amount">
          <span className="font-black" style={{ fontSize: '10.5px' }}>{p.amount.label}</span>
          <span className="font-mono font-black" style={{ fontSize: '13px' }} dir="ltr">{p.amount.value}</span>
        </div>
      ) : null}

      {/* Totals */}
      {p.totals && p.totals.length > 0 && (
        <div className="mt-1" style={{ fontSize: '9.5px' }}>
          {p.totals.map((r, i) => (
            <div key={i} className="flex justify-between py-0.5 border-b border-dashed border-black">
              <span className="font-bold">{r.label}</span>
              <span className={`font-mono ${r.strong ? 'font-black' : 'font-bold'}`} dir="ltr">{r.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Tafqeet */}
      {p.words && p.words.length > 0 && (
        <div className="mt-1.5 text-center font-bold" style={{ fontSize: '9px', letterSpacing: 'normal' }} data-testid="v80-words">
          {p.words.map((w, i) => (
            <p key={i}>{w}</p>
          ))}
        </div>
      )}

      {p.note ? (
        <p className="mt-1.5 font-medium" style={{ fontSize: '9px', wordBreak: 'break-word' }}>
          <span className="font-bold">{t('print.v80.note')}: </span>
          {p.note}
        </p>
      ) : null}

      {/* Footer */}
      <div className="border-t border-dashed border-black mt-2 pt-1.5 flex justify-between items-end" style={{ fontSize: '9px' }}>
        <div>
          <p className="font-bold">{t('print.v80.operator')}: <span className="font-mono font-black">{p.operator}</span></p>
          {p.printedLabel ? <p className="font-mono" dir="ltr">{t('print.v80.printed')} {p.printedLabel}</p> : null}
        </div>
        <div className="text-center">
          <p className="font-black">{t('print.v80.signature')}</p>
          <div className="border-b border-dashed border-black mt-4" style={{ width: '26mm' }}></div>
        </div>
      </div>
      <p className="text-center font-bold mt-2" style={{ fontSize: '9px' }}>{t('print.v80.thanks')}</p>
      <div className="border-t border-dashed border-black mt-1"></div>
    </div>
  );
}
